import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  Package,
  DollarSign,
  FileText,
  Cog,
  LogOut,
  User,
  CreditCard,
  ShieldCheck
} from 'lucide-react';
import DashboardMain from './components/DashboardMain';
import Settings from './components/settings.jsx';
import Documents from './components/Documents';
import Produtos from './components/Produtos';
import Vendas from './components/Vendas';
import Login from './components/Login';
import LandingPage from './components/LandingPage';
import UserProfile from './components/UserProfile';
import Billing from './components/Billing';
import Admin from './components/Admin';
import SubscriptionBanner, { useSubscription } from './components/SubscriptionBanner';
import BlogList from './components/Blog/BlogList';
import BlogPost from './components/Blog/BlogPost';
import Sobre from './components/StaticPages/Sobre';
import Privacidade from './components/StaticPages/Privacidade';
import Contato from './components/StaticPages/Contato';
import { MLProvider } from './contexts/MLContext';
import { ValueVisibilityProvider } from './contexts/ValueVisibilityContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Componente para mostrar status da conexão

function LogoutButton({ isOpen }) {
  const { user, logout } = useAuth();
  return (
    <button
      onClick={logout}
      title="Sair"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        justifyContent: isOpen ? 'flex-start' : 'center',
        width: '100%',
        padding: '8px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#999',
        borderRadius: '4px',
        fontSize: '14px',
      }}
    >
      <LogOut size={20} />
      {isOpen && <span>{user?.name || 'Sair'}</span>}
    </button>
  );
}

// Componente Sidebar
function Sidebar({ isOpen, toggleSidebar, sub, user }) {
  const location = useLocation();

  const billingColor = !sub?.invoice ? null
    : sub.status === 'blocked' ? '#cc0000'
    : sub.status === 'warning' ? '#f59e0b'
    : '#16a34a';

  const menuItems = [
    { path: '/', icon: <LayoutDashboard size={20} />, text: 'Dashboard' },
    { path: '/products', icon: <Package size={20} />, text: 'Produtos' },
    { path: '/sales', icon: <DollarSign size={20} />, text: 'Vendas' },
    { path: '/documents', icon: <FileText size={20} />, text: 'Documentos' },
    { path: '/settings', icon: <Cog size={20} />, text: 'Configurações' },
    { path: '/profile', icon: <User size={20} />, text: 'Minha Conta' },
    ...(billingColor ? [{ path: '/billing', icon: <CreditCard size={20} />, text: 'Pagar mensalidade', color: billingColor }] : []),
    ...(user?.isAdmin ? [{ path: '/admin', icon: <ShieldCheck size={20} />, text: 'Admin', color: '#7c3aed' }] : [])
  ];

  return (
    <div style={{
      width: isOpen ? '200px' : '60px',
      backgroundColor: '#ffffff',
      borderRight: '1px solid #ddd',
      transition: 'width 0.3s ease',
      position: 'relative',
      height: '100%',
    }}
   >
     <button
       onClick={toggleSidebar}
       style={{
         position: 'absolute',
         right: '-12px',
         top: '20px',
         width: '24px',
         height: '24px',
         backgroundColor: '#ffffff',
         border: '1px solid #ddd',
         borderRadius: '50%',
         cursor: 'pointer',
         display: 'flex',
         alignItems: 'center',
         justifyContent: 'center',
         zIndex: 10,
         padding: 0
       }}
     >
       {isOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
     </button>

     <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', height: 'calc(100% - 40px)' }}>
       <div style={{
         marginBottom: '30px',
         display: 'flex',
         alignItems: 'center',
         justifyContent: isOpen ? 'flex-start' : 'center'
       }}>
         {isOpen ? (
           <img src="/logo-integra-mkp.png" alt="Integra MKP" style={{ width: '140px', height: 'auto' }} />
         ) : (
           <img src="/logo-integra-mkp.png" alt="Integra MKP" style={{ width: '36px', height: 'auto', objectFit: 'cover', objectPosition: 'left' }} />
         )}
       </div>

       <div style={{ flex: 1 }}>
         {menuItems.map((item) => {
           const activeColor = item.color || '#2D3277';
           const isActive = location.pathname === item.path;
           return (
             <Link
               key={item.path}
               to={item.path}
               style={{ textDecoration: 'none', color: isActive ? activeColor : (item.color || '#666') }}
             >
               <div style={{
                 marginBottom: '15px',
                 cursor: 'pointer',
                 display: 'flex',
                 alignItems: 'center',
                 padding: '8px',
                 backgroundColor: isActive ? '#f5f5f5' : 'transparent',
                 borderRadius: '4px',
                 gap: '10px',
                 justifyContent: isOpen ? 'flex-start' : 'center'
               }}>
                 {item.icon}
                 {isOpen && <span style={{ fontSize: '14px', fontWeight: item.color ? 600 : 400 }}>{item.text}</span>}
               </div>
             </Link>
           );
         })}
       </div>

       <LogoutButton isOpen={isOpen} />
     </div>
   </div>
 );
}

const BANNER_HEIGHT = '38px';

// Componente Layout
function Layout({ children, menuOpen, setMenuOpen, sub, user }) {
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const hasBanner = sub?.status === 'trial' || sub?.status === 'warning';

  return (
    <div style={{
      display: 'flex',
      height: hasBanner ? `calc(100vh - ${BANNER_HEIGHT})` : '100vh',
      width: '100%',
      position: 'fixed',
      top: hasBanner ? BANNER_HEIGHT : 0,
      left: 0
  }}>
      <Sidebar isOpen={menuOpen} toggleSidebar={toggleMenu} sub={sub} user={user} />
      <div style={{ 
          flex: 1, 
          backgroundColor: '#FFFFFF',
          overflow: 'auto',
          position: 'relative'  // Para o conteúdo se ajustar corretamente
      }}>
          {children}
      </div>
  </div>
  );
}

function AppInner() {
  const { user, loading } = useAuth();
  const [menuOpen, setMenuOpen] = useState(true);
  const { sub, refreshSub } = useSubscription();

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f2f5' }}>
        <div style={{ color: '#2D3277', fontSize: '1rem' }}>Carregando...</div>
      </div>
    );
  }

  // Rotas públicas — acessíveis sem login
  const pathname = window.location.pathname;
  const isPublicPath = pathname.startsWith('/blog') || pathname === '/sobre' || pathname === '/privacidade' || pathname === '/contato';

  if (!user && !isPublicPath) return <LandingPage />;

  // Rotas públicas sempre sem sidebar, independente de estar logado
  if (isPublicPath) {
    return (
      <Router>
        <Routes>
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/sobre" element={<Sobre />} />
          <Route path="/privacidade" element={<Privacidade />} />
          <Route path="/contato" element={<Contato />} />
        </Routes>
      </Router>
    );
  }

  return (
    <MLProvider>
      <ValueVisibilityProvider>
        <Router>
          <SubscriptionBanner sub={sub} />
          <Layout menuOpen={menuOpen} setMenuOpen={setMenuOpen} sub={sub} user={user}>
            <Routes>
              <Route path="/" element={<DashboardMain menuOpen={menuOpen} />} />
              <Route path="/products" element={<Produtos />} />
              <Route path="/sales" element={<Vendas />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/profile" element={<UserProfile />} />
              <Route path="/billing" element={<Billing onPaymentSuccess={refreshSub} />} />
              <Route path="/admin" element={<Admin />} />
            </Routes>
          </Layout>
        </Router>
      </ValueVisibilityProvider>
    </MLProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}

export default App;